import { useState } from 'react';
import type { MeerwerkDto } from '@/api/generated';
import {
  useApproveMeerwerk,
  useExportMeerwerkenCsv,
  useMeerwerken,
  useRejectMeerwerk,
} from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { Icon } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { formatNumberNlBE, toNumber } from '@/lib/format';
import { MeerwerkCreateDialog, MeerwerkEditDialog } from './MeerwerkDialogs';
import styles from './Meerwerken.module.css';

interface KpiProps {
  label: string;
  value: number;
  unit: string;
  sub: string;
  kind?: 'warn' | 'ok' | 'bad';
}

function Kpi({ label, value, unit, sub, kind }: KpiProps) {
  return (
    <Card className={styles.kpi}>
      <div className="eyebrow">{label}</div>
      <div className={`row gap-4 ${styles.kpiValue}`}>
        <span className={styles.kpiUnit}>{unit}</span>
        <span className={styles.kpiNumber}>{formatNumberNlBE(value)}</span>
      </div>
      <div className={`mono ${styles.kpiSub} ${kind ? `c-${kind}` : ''}`}>{sub}</div>
    </Card>
  );
}

const STATUS_LABEL: Record<MeerwerkDto['status'], string> = {
  Voorgesteld: 'voorgesteld',
  Goedgekeurd: 'akkoord',
  Afgewezen: 'afgewezen',
};

export function Meerwerken() {
  const all = useMeerwerken({ pageSize: 50 });
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MeerwerkDto | null>(null);
  const exportCsv = useExportMeerwerkenCsv();

  return (
    <AppFrame>
      <QueryBoundary query={all} loading="Meerwerken laden…">
        {(page) => {
          const items = page.items;
          const akkoord = items.filter((m) => m.status === 'Goedgekeurd');
          const voorstellen = items.filter((m) => m.status === 'Voorgesteld');
          const totaal = akkoord.reduce((s, m) => s + toNumber(m.amount), 0);
          const voorstellenSom = voorstellen.reduce((s, m) => s + toNumber(m.amount), 0);

          return (
            <>
              <div className={`row ${styles.headerRow}`}>
                <div>
                  <div className="eyebrow">
                    Meerwerken-register · {akkoord.length} akkoord · {voorstellen.length} voorstel
                  </div>
                  <h1 className={styles.title}>Meerwerken</h1>
                  <p className={styles.subtitle}>
                    Meerwerken is meestal het grootste budgetlek. Hier wordt elk meer- of minderwerk
                    vastgelegd <em>voor</em> de uitvoering, met datum en handtekening.
                  </p>
                </div>
                <div className={`ml-auto row gap-8 ${styles.headerActions}`}>
                  <Btn onClick={() => exportCsv.mutate()} disabled={exportCsv.isPending}>
                    <Icon name="download" size={14} /> {exportCsv.isPending ? 'Bezig…' : 'Export'}
                  </Btn>
                  <Btn variant="primary" onClick={() => setCreateOpen(true)}>
                    <Icon name="plus" size={14} /> Meerwerk
                  </Btn>
                </div>
              </div>
              <MeerwerkCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
              {editTarget && (
                <MeerwerkEditDialog
                  open={editTarget !== null}
                  onOpenChange={(o) => {
                    if (!o) setEditTarget(null);
                  }}
                  meerwerk={editTarget}
                />
              )}

              <div className={styles.kpiStrip}>
                <Kpi
                  label="Totaal goedgekeurd"
                  value={totaal}
                  unit="€"
                  sub={`${akkoord.length} items`}
                />
                <Kpi
                  label="Wacht op akkoord"
                  value={voorstellenSom}
                  unit="€"
                  sub={`${voorstellen.length} voorstel`}
                  kind="warn"
                />
                <Kpi
                  label="% van bouwbudget"
                  value={Math.round((totaal / 520_000) * 100)}
                  unit="%"
                  sub="onvoorzien-post heeft 5%"
                />
                <Kpi
                  label="Saldo onvoorzien"
                  value={Math.max(0, 26_000 - totaal)}
                  unit="€"
                  sub="van €26 000"
                  kind="ok"
                />
              </div>

              {voorstellen.map((v) => (
                <ProposalRow key={v.id} meerwerk={v} onEdit={setEditTarget} />
              ))}

              <Card className={styles.tableCard}>
                <div className={`row gap-12 ${styles.tableHead}`}>
                  <div className={styles.colNr}>Nr</div>
                  <div className={styles.colDesc}>Omschrijving</div>
                  <div className={styles.colPhase}>Fase</div>
                  <div className={styles.colAmount}>Bedrag</div>
                  <div className={styles.colOk}>Status</div>
                </div>
                {akkoord.map((it) => (
                  <ApprovedRow key={it.id} meerwerk={it} onEdit={setEditTarget} />
                ))}
                {akkoord.length === 0 && (
                  <div className="mono c-ink-3" style={{ padding: '24px' }}>
                    Nog geen goedgekeurde meerwerken.
                  </div>
                )}
                <div className={`row gap-12 ${styles.totalsRow}`}>
                  <div className={styles.totalsLabel}>Totaal goedgekeurde meerwerken</div>
                  <div className={`mono ${styles.colAmount}`}>+€{formatNumberNlBE(totaal)}</div>
                  <div className={styles.colOk} />
                </div>
              </Card>
            </>
          );
        }}
      </QueryBoundary>
    </AppFrame>
  );
}

function ProposalRow({
  meerwerk,
  onEdit,
}: {
  meerwerk: MeerwerkDto;
  onEdit: (m: MeerwerkDto) => void;
}) {
  const approve = useApproveMeerwerk();
  const reject = useRejectMeerwerk();
  const busy = approve.isPending || reject.isPending;
  return (
    <div className={styles.proposal}>
      <div className="row gap-12">
        <Icon name="flag" size={16} color="var(--accent-2)" />
        <div className="flex-1">
          <div className="row gap-8">
            <span className={`mono ${styles.propNr}`}>{meerwerk.id.slice(0, 6)}</span>
            <h3 className={styles.propTitle}>{meerwerk.title}</h3>
            <Pill kind="idle" dot={false}>
              {meerwerk.phaseId}
            </Pill>
          </div>
          <p className={styles.propNote}>Voorstel ter goedkeuring.</p>
        </div>
        <div className={styles.propAmountBox}>
          <div className={styles.propAmount}>+€{formatNumberNlBE(toNumber(meerwerk.amount))}</div>
          <div className={`mono ${styles.propAmountSub}`}>excl. BTW</div>
        </div>
        <div className="col gap-6">
          <Btn
            variant="primary"
            className={styles.propBtn}
            onClick={() => approve.mutate(meerwerk.id)}
            disabled={busy}
          >
            <Icon name="check" size={13} /> Akkoord
          </Btn>
          <Btn
            variant="ghost"
            className={`${styles.propBtn} c-bad`}
            onClick={() => reject.mutate(meerwerk.id)}
            disabled={busy}
          >
            Afwijzen
          </Btn>
          <Btn
            variant="ghost"
            className={styles.propBtn}
            onClick={() => onEdit(meerwerk)}
            disabled={busy}
          >
            Bewerk
          </Btn>
        </div>
      </div>
    </div>
  );
}

function ApprovedRow({
  meerwerk,
  onEdit,
}: {
  meerwerk: MeerwerkDto;
  onEdit: (m: MeerwerkDto) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const amt = toNumber(meerwerk.amount);
  return (
    <div className={`row gap-12 ${styles.tableRow}`}>
      <div className={`mono ${styles.colNr} ${styles.muted}`}>{meerwerk.id.slice(0, 6)}</div>
      <div className={styles.colDesc}>
        <div className={styles.itemTitle}>{meerwerk.title}</div>
      </div>
      <div className={styles.colPhase}>
        <Pill kind="idle" dot={false}>
          {meerwerk.phaseId}
        </Pill>
      </div>
      <div
        className={`mono ${styles.colAmount} ${styles.itemAmount} ${
          amt < 0 ? 'c-ok' : amt === 0 ? styles.muted : ''
        }`}
      >
        {amt < 0 ? '−' : amt > 0 ? '+' : ''}€{formatNumberNlBE(Math.abs(amt))}
      </div>
      <div className={`row gap-6 ${styles.colOk}`}>
        <Pill kind="ok" dot={false}>
          ✓ {STATUS_LABEL[meerwerk.status]}
        </Pill>
        <div className={styles.kebabWrap}>
          <button
            type="button"
            className={styles.kebabBtn}
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Meer acties"
          >
            <Icon name="more" size={14} />
          </button>
          {menuOpen && (
            <>
              <button
                type="button"
                className={styles.kebabBackdrop}
                aria-label="Sluit menu"
                tabIndex={-1}
                onClick={() => setMenuOpen(false)}
              />
              <div role="menu" className={styles.kebabMenu}>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onEdit(meerwerk);
                  }}
                >
                  Bewerk
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
